import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../SEO'
import { SplitSection, StackRibbon } from '../ServicePage'
import ContactForm from '../ContactForm'
import ovoLogo from '../../assets/clientLogos/ovo_transparent_logo.png'
import { trackCtaClick, trackOutboundClick } from '../../utils/analytics'
import './../../styles/CaseStudy.css'

/* Hero visual — sun + wind blades + meter, signalling green energy + data */
const OvoHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg" className="cs-hero__svg" aria-hidden="true">
    <defs>
      <linearGradient id="ovo-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00c851" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="ovo-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="230" cy="230" r="200" fill="url(#ovo-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* Sun */}
    <g transform="translate(140 140)">
      <circle r="38" fill="url(#ovo-grad)" />
      <g stroke="url(#ovo-grad)" strokeWidth="3" strokeLinecap="round">
        <line x1="0" y1="-58" x2="0" y2="-72" />
        <line x1="0" y1="58" x2="0" y2="72" />
        <line x1="-58" y1="0" x2="-72" y2="0" />
        <line x1="58" y1="0" x2="72" y2="0" />
        <line x1="-42" y1="-42" x2="-52" y2="-52" />
        <line x1="42" y1="42" x2="52" y2="52" />
        <line x1="-42" y1="42" x2="-52" y2="52" />
        <line x1="42" y1="-42" x2="52" y2="-52" />
      </g>
      <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="60s" repeatCount="indefinite" />
    </g>

    {/* Wind turbine */}
    <g transform="translate(330 320)">
      <line x1="0" y1="0" x2="0" y2="100" stroke="rgba(255,255,255,0.35)" strokeWidth="3" strokeLinecap="round" />
      <g>
        <path d="M0 0 L26 -52 L8 -8 Z" fill="rgba(0,212,255,0.85)" />
        <path d="M0 0 L-46 -28 L-8 8 Z" fill="rgba(0,212,255,0.65)" />
        <path d="M0 0 L20 56 L-2 12 Z" fill="rgba(0,212,255,0.75)" />
        <circle r="6" fill="#fff" />
        <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="6s" repeatCount="indefinite" />
      </g>
    </g>

    {/* Data card */}
    <g transform="translate(80 280)">
      <rect width="170" height="100" rx="14" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.18)" />
      <rect x="14" y="16" width="100" height="8" rx="3" fill="rgba(0,212,255,0.65)" />
      <rect x="14" y="32" width="60" height="6" rx="2" fill="rgba(255,255,255,0.40)" />
      <rect x="14" y="60" width="142" height="6" rx="2" fill="rgba(255,255,255,0.10)" />
      <rect x="14" y="60" width="92" height="6" rx="2" fill="url(#ovo-grad)">
        <animate attributeName="width" values="20;120;20" dur="5s" repeatCount="indefinite" />
      </rect>
      <text x="14" y="86" fontFamily="JetBrains Mono, monospace" fontSize="11" fill="rgba(255,255,255,0.55)">SEG · kWh · £</text>
    </g>
  </svg>
)

const pressLinks = [
  {
    href: 'https://company.ovo.com/here-comes-the-sun-ovo-solar-will-help-customers-save-over-90-on-energy-bills/',
    src: 'OVO Group',
    title: 'Here comes the sun — OVO Solar will help customers save over 90% on energy bills',
  },
  {
    href: 'https://www.ovoenergy.com/smart-export-guarantee',
    src: 'OVO Energy',
    title: 'Smart Export Guarantee Tariff (SEG) — get paid for the energy you export',
  },
  {
    href: 'https://forum.ovoenergy.com/home-and-heating-138/the-smart-export-guarantee-seg-getting-paid-for-the-energy-you-export-to-the-grid-faqs-8635',
    src: 'OVO Forum',
    title: 'The Smart Export Guarantee — getting paid for the energy you export to the grid',
  },
]

const OVO = () => (
  <>
    <SEO
      path="/case-studies/ovo"
      title="OVO Energy — Greenfield QA, ETL & Snowflake Data Validation"
      description="QA Tech led the QA, automation, ETL and Snowflake data validation programme for OVO Energy's greenfield green-energy customer portal — covering Smart Export Guarantee, solar & wind tariffs, and complex billing reconciliation."
      keywords="OVO Energy case study, energy QA, Smart Export Guarantee QA, Snowflake testing, ETL testing, Cypress BDD, Playwright API, green energy software testing, fintech billing testing"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline:
          'OVO Energy — greenfield customer portal, ETL and Snowflake data validation',
        author: { '@type': 'Organization', name: 'QA Tech' },
        publisher: { '@type': 'Organization', name: 'QA Tech', url: 'https://qatechltd.com' },
        about: ['OVO Energy', 'Smart Export Guarantee', 'Snowflake', 'ETL testing', 'Energy'],
        url: 'https://qatechltd.com/case-studies/ovo',
      }}
    />

    <div className="body cs-body">
      <header className="cs-hero">
        <div className="cs-hero__bg" aria-hidden="true">
          <span className="cs-hero__orb cs-hero__orb--a" />
          <span className="cs-hero__orb cs-hero__orb--b" />
          <span className="cs-hero__grid" />
        </div>

        <div className="cs-hero__inner">
          <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link><span aria-hidden="true">/</span>
            <Link to="/case-studies">Case Studies</Link><span aria-hidden="true">/</span>
            <span>OVO Energy</span>
          </nav>

          <div className="cs-hero__content">
            <div className="cs-hero__client">
              <img src={ovoLogo} alt="OVO Energy" className="cs-hero__logo" loading="lazy" />
              <div className="cs-hero__client-text">
                <span className="eyebrow">Case study</span>
                <h2 className="cs-hero__client-name">OVO Energy</h2>
                <p className="cs-hero__client-sub">UK&rsquo;s largest independent energy supplier &mdash; greenfield green-energy portal</p>
              </div>
            </div>

            <h1 className="cs-hero__title">
              Greenfield portal. Real billing. <span className="cs-hero__title-accent">Real data, validated.</span>
            </h1>

            <p className="cs-hero__lede">
              We led QA, ETL and data validation on a brand-new portal that
              lets OVO customers and customer-service agents manage solar
              and wind generation, exports, and complex green-tariff billing
              &mdash; all on top of a Snowflake-backed analytics layer.
            </p>

            <div className="cs-hero__cta-row">
              <a
                href="#cs-contact"
                className="sp-cta-primary"
                onClick={() => trackCtaClick('Work with us', 'case_study_ovo_hero')}
              >
                Work with us
              </a>
              <a href="#cs-overview" className="sp-cta-secondary">Read the case study &darr;</a>
            </div>
          </div>

          <div className="cs-hero__visual" aria-hidden="true">
            <OvoHero />
          </div>
        </div>
      </header>

      <section className="cs-stats" aria-label="Project at a glance" id="cs-overview">
        <div className="cs-stats__inner">
          <div className="cs-stat"><span className="cs-stat__num">Greenfield</span><span className="cs-stat__label">portal built from zero</span></div>
          <div className="cs-stat"><span className="cs-stat__num">Delivered</span><span className="cs-stat__label">QA, ETL &amp; data validation</span></div>
          <div className="cs-stat"><span className="cs-stat__num">Snowflake</span><span className="cs-stat__label">data &amp; ETL validated</span></div>
          <div className="cs-stat"><span className="cs-stat__num">End-to-end</span><span className="cs-stat__label">billing &amp; reconciliation</span></div>
        </div>
      </section>

      <main className="cs-main">
        <SplitSection index={1} align="left" kicker="The brief" title="A green-energy portal, built right.">
          <p>
            OVO needed a brand-new portal for customers and customer-service
            agents to manage accounts, billing, and the complex business of
            energy exported back to the grid via solar, wind and the
            <strong> Smart Export Guarantee</strong>. We were brought in as
            QA leadership to set the quality bar and architect the testing
            programme from day zero.
          </p>
        </SplitSection>

        <SplitSection index={2} align="right" kicker="Test infrastructure" title="From zero to a working pyramid.">
          <p>
            We pioneered the test infrastructure from scratch &mdash; tooling
            choices, environments, fixtures, CI integration and the
            in-team conventions that made it actually used.
          </p>
          <ul>
            <li>QA leadership across both manual and automated streams</li>
            <li>Test pyramid spanning unit, component, integration, API and end-to-end</li>
            <li>Shift-left culture &mdash; tests written alongside code, not after</li>
            <li>Shared fixtures and seed data across environments</li>
          </ul>
        </SplitSection>

        <SplitSection index={3} align="left" kicker="UI &amp; API automation" title="Cypress with BDD on the front, Playwright on the API.">
          <p>
            A Cypress + Cucumber BDD suite covered the customer and CSA
            journeys in TypeScript, with strict adherence to SOLID
            principles so the tests stayed maintainable as the product
            grew. On the API side, we built a Playwright-based REST
            automation framework alongside extensive Postman work for
            CRUD operations and integration testing.
          </p>
          <ul>
            <li>Cypress + Cucumber BDD for UI flows</li>
            <li>Playwright API library for REST automation</li>
            <li>Postman collections for integration &amp; CRUD ops</li>
            <li>SOLID-driven test architecture</li>
          </ul>
        </SplitSection>

        <SplitSection index={4} align="right" kicker="ETL &amp; big data" title="Validating the data engine behind every bill.">
          <p>
            The portal sits on top of an analytics platform handling solar,
            wind, customer and payment datasets. We supported validation of
            big-data pipelines end-to-end &mdash; ingest, transform, report
            &mdash; making sure every number that hit a customer&rsquo;s
            invoice was accurate and reconciled.
          </p>
          <ul>
            <li>End-to-end ETL validation across raw, transformed and reporting layers</li>
            <li>Snowflake-backed data models and batch jobs validated against rule sets</li>
            <li>Identified mismatches in mappings, aggregation logic and downstream reporting</li>
            <li>Heavy use of SQL for spot-checks and reconciliation</li>
          </ul>
        </SplitSection>

        <SplitSection index={5} align="left" kicker="Payments &amp; financial operations" title="Payments, refunds, exports, reconciliation.">
          <p>
            Energy billing is more than a sum &mdash; it&rsquo;s tariffs,
            refunds, reconciliation, levelisation, exports, and a
            different ruleset for commercial vs personal customers. We
            architected the financial-operations test programme so every
            calculation could be defended in writing.
          </p>
          <p>
            <strong>Payment journeys.</strong> Direct-debit setup,
            amendment and cancellation; card-on-file management; bill-pay
            confirmations; top-up flows for prepay segments; mid-cycle
            payment-method changes; failed-payment retries; refund
            issuance and partial bill adjustments. Plus the{' '}
            <strong>Smart Export Guarantee</strong> export-payment
            journey end-to-end &mdash; where the customer gets <em>paid</em>
            for the energy they put back into the grid via solar or wind.
          </p>
          <p>
            <strong>Payment integrations.</strong> Validated the
            integration layer behind those journeys &mdash; direct-debit
            cycles with the clearing system, card-processor webhooks,
            statement generation, VAT handling on commercial accounts,
            and reconciliation across the raw, transformed and reporting
            layers in Snowflake so the finance team could trust the
            books down to the penny.
          </p>
          <ul>
            <li>Direct-debit setup, amendment &amp; cancellation flows</li>
            <li>Card payments, saved-card management &amp; mid-cycle changes</li>
            <li>Smart Export Guarantee export-payment journey end-to-end</li>
            <li>Levelisation &amp; billing-period edge cases</li>
            <li>Refunds, credits and partial bill adjustments</li>
            <li>Statement generation &amp; VAT handling for commercial accounts</li>
            <li>Reconciliation across raw, transformed &amp; reporting Snowflake layers</li>
            <li>Permission-based and feature-flag testing across admin tiers</li>
          </ul>
        </SplitSection>

        <SplitSection index={6} align="right" kicker="Performance &amp; accessibility" title="Lighthouse, k6, and an inclusive UI.">
          <p>
            Performance matters when customers are checking bills on their
            phones at the kitchen table. We used Google Lighthouse for UI
            performance benchmarks, k6 for backend API load and stress
            testing, and ran non-functional accessibility testing to keep
            the portal usable for everyone.
          </p>
        </SplitSection>

        <SplitSection index={7} align="left" kicker="CI/CD &amp; ways of working" title="GitHub Actions, 3 amigos, agile delivery.">
          <p>
            We built CI pipelines in GitHub Actions with tests as a
            first-class quality gate, championed 3-amigo meetings for
            requirements and bug triage, and led the test &amp; QA
            requirements gathering inside the team&rsquo;s Scrum cadence.
          </p>
        </SplitSection>

        <StackRibbon
          label="What we delivered"
          items={[
            'Test strategy', 'Test infrastructure from scratch',
            'Cypress + Cucumber BDD', 'Playwright API',
            'Postman / REST', 'ETL &amp; big-data validation',
            'Snowflake data validation', 'SQL reconciliation',
            'Payment journey testing (direct debit, card, SEG export)',
            'Payment integration validation (clearing, webhooks, statements)',
            'Financial-operations testing &amp; reconciliation',
            'Feature-flag &amp; permission testing',
            'Performance (Lighthouse, k6)', 'Accessibility',
            'CI/CD (GitHub Actions)', 'Manual + automated QA leadership',
          ]}
        />

        <StackRibbon
          label="Tech &amp; tools"
          items={[
            'TypeScript', 'JavaScript',
            'Cypress', 'Cucumber',
            'Playwright', 'Postman',
            'Snowflake', 'SQL',
            'Docker',
            'k6', 'Google Lighthouse',
            'GitHub Actions', 'GitHub',
          ]}
        />

        <section className="cs-press" aria-label="Press and coverage">
          <div className="cs-press__inner">
            <span className="sp-stack__label">Press &amp; coverage</span>
            <p className="cs-press__lead">
              Selected reporting and official material on the OVO Solar /
              SEG programme this work supported.
            </p>
            <ul className="cs-press__list">
              {pressLinks.map((p) => (
                <li key={p.href}>
                  <a href={p.href} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackOutboundClick(p.href, p.title, 'case_study_ovo_press')}>
                    <span className="cs-press__src">{p.src}</span>
                    <span className="cs-press__title">{p.title}</span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="cs-press__note">
              External links open in a new tab. Coverage attributed to its
              respective publishers; QA Tech is independent and not affiliated
              with OVO Energy.
            </p>
          </div>
        </section>
      </main>

      <section className="sp-cta-section">
        <div className="sp-cta-section__inner">
          <h2>Got a high-stakes data programme?</h2>
          <p>If your business runs on numbers customers can see, we&rsquo;d love to talk.</p>
        </div>
      </section>

      <div id="cs-contact"><ContactForm /></div>
    </div>
  </>
)

export default OVO
